import _, { capitalize, get, has, isEmpty, toLower } from "lodash";
import { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { InlineError } from "../components/Field";
import Radio from "../components/Radio";
import StepBody from "../components/StepBody";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";
import { MFA_VALID_ENROLLMENT_MESSAGE } from "../constants";
import { getMfaOptions, isAwaiting, isStarted, processDirectLink, sendMfa } from "../lib/axle";
import { MAX_HITL_STARTED_RETRIES } from "../lib/constants";
import { isIntegrationV123, useAccountHandler } from "../lib/utility";
import CarrierLoader from "./CarrierLoader";

// A dropped connection — API Gateway's ~29s REST ceiling, well short of what a
// browser-agent goal can take — doesn't mean the fetch failed. This bounds the
// hiccups absorbed, not time waited: the retries re-fire immediately. Waiting
// on the agent is paced by ~20s polls and bounded by MAX_HITL_STARTED_RETRIES.
const MAX_GET_MFA_OPTIONS_ATTEMPTS = 5;

const SendMfa = ({
  step,
  nextStep,
  setLoginInformation,
  setAccountInfo,
  loginInformation,
  session,
  setLinkError,
  posthog,
  setShowNav,
  integration,
}) => {
  const carrier = get(loginInformation, "carrier.id");
  const ignitionToken = session.id;
  let getMfaOptionsAttempts = 0;
  let startedPollAttempts = 0;

  const [error, setError] = useState(null);
  let [mfaOptions, setMfaOptions] = useState([]);

  // Configure carrier loader
  const { promiseInProgress } = usePromiseTracker({ area: "carrier-loader" });
  const loadingSteps = [
    "Establishing a secure connection",
    "Contacting your carrier",
    "Retrieving verification options",
  ];

  // Wired exactly as EnterMfa wires it, and deliberately WITHOUT
  // setLoginInformation: passing it makes the handler stamp result: "link", which
  // isIntegrationV123 reads as "legacy run" and would send Confirm down linkPolicy
  // instead of completing the browser agent's policy-wait. This is also why the
  // skip lives here and not in Login, whose handler does pass it.
  const getAccount = useAccountHandler({
    ignitionToken,
    integration,
    setAccountInfo,
    loginInformation,
    nextStep,
    setLinkError,
    setShowNav,
    retryGetAccountAttempts: 0,
  });

  const onClick = async (index) => {
    let { type, id } = mfaOptions[index];

    // Send MFA code
    
    if (isIntegrationV123(loginInformation, integration, session)) {
      const operation = "sendMfa"
      
      const [sendMfaResponse, sendMfaError] = await trackPromise(processDirectLink(
        ignitionToken,
        integration,
        operation,
        { selectedMfaOption: type } 
      ), "carrier-loader")
    } else {
      const [sendMfaResponse, sendMfaError] = await trackPromise(
        sendMfa(ignitionToken, carrier, integration, type, id),
        "carrier-loader"
      );
      if (sendMfaError) {
        switch (get(sendMfaError, "code")) {
          // If session expired, send customer to session-expired
          case 401:
          case 403:
            setLinkError(sendMfaError.message);
            nextStep("session-expired");
            return;
          case 500:
            setLinkError("send MFA code");
            nextStep("carrier-error");
            return;
          default:
            nextStep("connection-error");
            return;
        }
      }
    }

    // Save MFA type to state
    setLoginInformation({ ...loginInformation, mfa: { type } });

    // Go to MFA code entry step
    nextStep("enterMfa");
  };

  const fetchMfaOptions = async () => {
    // Hide nav buttons while CarrierLoader is rendered for accessibility
    setShowNav(false);

    if (isIntegrationV123(loginInformation, integration, session)) {
      const operation = "getMfaOptions"

      const [mfaOptionsResponse, mfaOptionsError] = await processDirectLink(
        ignitionToken,
        integration,
        operation,
        {} // no data needed for getMfaOptions
      )

      if (mfaOptionsError) {
        switch (get(mfaOptionsError, "code")) {
          // If session expired, send customer to session-expired
          case 401:
          case 403:
            setLinkError(mfaOptionsError.message);
            nextStep("session-expired");
            return;
          case 400:
            // The run already ended on a member-side MFA state, so retrying replays it.
            if (mfaOptionsError.message === MFA_VALID_ENROLLMENT_MESSAGE) {
              setLinkError(mfaOptionsError.message);
              nextStep("mfa-options-error");
              return;
            }
          // falls through
          default:
            // A dropped connection (e.g. the API Gateway 504 the route's
            // ~29s ceiling can produce) or a transient 500 doesn't mean the
            // browser agent failed — retry before surfacing a hard error.
            if (getMfaOptionsAttempts < MAX_GET_MFA_OPTIONS_ATTEMPTS) {
              getMfaOptionsAttempts++;
              console.log("Received an error from getMfaOptions, retrying...");
              await fetchMfaOptions();
              return;
            }
            setLinkError("retrieve MFA options");
            nextStep("carrier-error");
            return;
        }
      }

      if (get(mfaOptionsResponse, "status") === "completed") {
        const options = get(mfaOptionsResponse, "mfaOptions");
        if (isEmpty(options)) {
          // `=== false` means the run ruled MFA out and is already extracting, so
          // no code is coming. Every other empty-options shape has a code in
          // flight and an armed code wait behind it, so the prompt is the default.
          if (get(mfaOptionsResponse, "mfaRequired") === false) {
            await getAccount();
            return;
          }
          nextStep("enterMfa");
          return;
        }
        setMfaOptions(options);
        // Show nav buttons after CarrierLoader is unrendered
        setShowNav(true);
        return;
      }

      // Not an error — the agent is still working (getMfaOptions just has no
      // re-entry prompt of its own to show). Poll on the same ~5min ceiling the
      // other HITL steps get: this covers the agent's navigation to the MFA
      // page, which login hands off mid-flight.
      if (isAwaiting(mfaOptionsResponse) || isStarted(mfaOptionsResponse)) {
        if (startedPollAttempts < MAX_HITL_STARTED_RETRIES) {
          startedPollAttempts++;
          await fetchMfaOptions();
          return;
        }
        nextStep("carrier-error");
        return;
      }

      nextStep("carrier-error");
      return;
    } else {
      const [mfaOptionsResponse, mfaOptionsError] = 
        await getMfaOptions(ignitionToken, carrier, integration)

        if (mfaOptionsError) {
          switch (get(mfaOptionsError, "code")) {
            // If session expired, send customer to session-expired
            case 400:
              if (
                mfaOptionsError.message &&
                mfaOptionsError.message.includes(MFA_VALID_ENROLLMENT_MESSAGE)
              ) {
                setLinkError(mfaOptionsError.message);
                nextStep("mfa-options-error");
              } else {
                setLinkError("retrieve MFA options");
                nextStep("carrier-error");
              }
              return;
            case 401:
            case 403:
              setLinkError(mfaOptionsError.message);
              nextStep("session-expired");
              return;
            // If 500 send customer to "failed"
            case 500:
              setLinkError("retrieve MFA options");
              nextStep("carrier-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }
    
        let mfaOptionsWithoutNulls;
        if (_.some(mfaOptionsResponse, (item) => _.isNil(item))) {
          console.error("mfaOptions contains null or undefined values");
          mfaOptionsWithoutNulls = _.compact(mfaOptionsResponse);
        } else {
          mfaOptionsWithoutNulls = mfaOptionsResponse;
        }
    
        setMfaOptions(mfaOptionsWithoutNulls);
        // Show nav buttons after CarrierLoader is unrendered
        setShowNav(true);
      
    }

    

    
  };

  useEffect(() => {
    // Track pageview
    posthog.capture("$pageview", { step, carrier });

    trackPromise(fetchMfaOptions(), "carrier-loader");
  }, [posthog]);

  return (
    <>
      {promiseInProgress ? (
        <CarrierLoader
          loginInformation={loginInformation}
          loadingHeader="Retrieving verification options"
          loadingSteps={loadingSteps}
        />
      ) : (
        <StepBody>
          <CarrierMark loginInformation={loginInformation} />
          <StepHeading
            title="Verify your identity"
            description={
              <>
                <span className="font-medium text-ink">
                  {loginInformation.carrier.name}
                </span>{" "}
                would like to verify your identity. How would you like to
                receive a verification code?
              </>
            }
          />
          <div className="flex flex-col gap-3">
            {error ? (
              <div className="rounded border border-danger-border p-3">
                <InlineError>{error}</InlineError>
              </div>
            ) : mfaOptions.length ? (
              mfaOptions.map((mfaOption, index) => (
                <Radio
                  key={index}
                  title={getMfaTypeLabel(mfaOption.type)}
                  capitalizeText={false}
                  subtext={mfaOption.label}
                  onClick={() => onClick(index)}
                />
              ))
            ) : (
              <p className="text-body text-ink-secondary">
                No verification options found.
              </p>
            )}
          </div>
          {/* <div className="absolute block bottom-0 left-0 w-full h-14 bg-gradient-to-t from-surface"></div> */}
        </StepBody>
      )}
    </>
  );
};

// Carrier MFA types arrive lowercase (e.g. "sms", "email"); acronyms need their own casing.
const MFA_TYPE_LABELS = { sms: "SMS" };

const getMfaTypeLabel = (type) =>
  MFA_TYPE_LABELS[toLower(type)] ?? capitalize(type);

export default SendMfa;
